import { buildNamespacedStorageKey } from '@/shared/data/cacheNamespace';
import type { GridColumnVisibilityModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import type { QueryKey } from '@tanstack/react-query';

export type GridColumnWidthModel = Record<string, number>;

export type PersistedState = {
  page?: number;
  pageSize?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnWidthModel?: GridColumnWidthModel;
};

export const parseUrlState = (
  searchParams: URLSearchParams,
  syncStateToUrl: boolean,
): {
  page?: number;
  pageSize?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
} | null => {
  if (!syncStateToUrl) return null;
  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const page = pageParam !== null ? Number(pageParam) : undefined;
  const pageSize = pageSizeParam !== null ? Number(pageSizeParam) : undefined;
  const sortRaw = searchParams.get('sort');
  const filterRaw = searchParams.get('filters');
  let sortModel: GridSortModel = [];
  let filterModel: GridFilterModel | null = null;
  try {
    if (sortRaw) sortModel = JSON.parse(sortRaw) as GridSortModel;
  } catch {
    sortModel = [];
  }
  try {
    if (filterRaw) filterModel = JSON.parse(filterRaw) as GridFilterModel;
  } catch {
    filterModel = null;
  }
  return {
    page: Number.isFinite(page) ? page : undefined,
    pageSize: Number.isFinite(pageSize) ? pageSize : undefined,
    sortModel,
    filterModel: filterModel ?? undefined,
  };
};

export const computeStorageKey = (
  persistKey: string | null | undefined,
  providedQueryKey: QueryKey | undefined,
  queryKeyPrefix: QueryKey | undefined,
  queryPath: string,
): string | null => {
  if (persistKey === null) return null;
  const key = persistKey ?? JSON.stringify(providedQueryKey ?? queryKeyPrefix ?? [queryPath]);
  return buildNamespacedStorageKey('datagrid', key);
};

export const getPersistedState = (storageKey: string | null): PersistedState | null => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
};

export const persistGridState = (
  storageKey: string | null,
  paginationModel: { page: number; pageSize: number },
  sortModel: GridSortModel,
  filterModel: GridFilterModel,
  columnVisibilityModel: GridColumnVisibilityModel,
  columnWidthModel: GridColumnWidthModel,
): void => {
  if (!storageKey) return;
  try {
    const payload: PersistedState = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sortModel,
      filterModel,
      columnVisibilityModel,
      columnWidthModel,
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
};
